import React, { useState, useEffect, useContext } from 'react';
import {
    Text,
    Dropdown,
    IDropdownOption,
    MessageBar,
    MessageBarType,
    Spinner,
    SpinnerSize,
    PrimaryButton,
    DefaultButton,
    TextField,
    Dialog,
    DialogType,
    DialogFooter,
    Stack,
    TooltipHost,
    Icon,
    IconButton,
} from '@fluentui/react';
import {
    EnvironmentData,
    DataChannelOverride,
    FlagOverride,
    EnvironmentFlags,
    Pipeline,
    Region,
    WorkLane,
} from '../models/EnvironmentModels';
import { ReleasePipelineSource } from '../../AGSS/models/ReleasePipelineSource';
import {
    getEnvironmentList,
    fetchEnvironmentData,
    saveEnvironmentData,
} from '../services/EnvironmentDataService';
import { useIsMobile } from '../../Common/hooks/useIsMobile';
import { ThemeContext } from '../../HubLayout/models/ThemeContext';
import { getThemeFromString } from '../../Common/util/localStorageUtils';
import { HubHeader } from '../../Common/components/HubHeader';
import {
    encodeFilterToClipboard,
    decodeFilterFromClipboard,
    encodeFlagOverrideToClipboard,
    decodeFlagOverrideFromClipboard,
    parseAzureDevOpsUrl,
    ParsedPipelineInfo,
} from '../utils/FilterClipboardUtils';
import { NormalOperationMode } from './NormalOperationMode';
import { DataChannelOverridesList } from './DataChannelOverridesList';
import { FeatureFlagOverridesList } from './FeatureFlagOverridesList';
import { ActiveChannelsList } from './ActiveChannelsList';
import { OperationModesList } from './OperationModesList';
import { ActiveConfigurationPreview } from './ActiveConfigurationPreview';
import { CsvImportDialog } from './CsvImportDialog';
import { DataChannelCsvImportDialog } from './DataChannelCsvImportDialog';
import {
    exportFlagOverridesToCsv,
    exportDataChannelOverridesToCsv,
    downloadCsv,
    generateCsvFilename,
} from '../utils/CsvUtils';
import '../../SRSDS/styles/CommonStyles.css';

// Helper function to convert string enum values to numeric values
const convertStringEnumToNumber = (
    value: any,
    enumType: 'Pipeline' | 'Region' | 'WorkLane' | 'ReleasePipelineSource'
): number | null => {
    if (value === null || value === undefined) return null;
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
        switch (enumType) {
            case 'Pipeline':
                if (value === 'Blob') return Pipeline.Blob;
                if (value === 'Core') return Pipeline.Core;
                if (value === 'Message') return Pipeline.Message;
                break;
            case 'Region':
                if (value === 'Primary') return Region.Primary;
                if (value === 'Secondary') return Region.Secondary;
                break;
            case 'WorkLane':
                if (value === 'Warm') return WorkLane.Warm;
                if (value === 'Dcmt') return WorkLane.Dcmt;
                break;
            case 'ReleasePipelineSource':
                if (value === 'Classic') return ReleasePipelineSource.Classic;
                if (value === 'YAML') return ReleasePipelineSource.YAML;
                if (value === 'Yaml') return ReleasePipelineSource.YAML; // Support both casings
                if (value === 'Generic') return ReleasePipelineSource.Generic;
                break;
        }
    }
    return null;
};

// Convert all enum values in the data structure
const normalizeEnumValues = (data: EnvironmentData): EnvironmentData => {
    const normalized = JSON.parse(JSON.stringify(data));

    // Normalize normal operation mode
    if (normalized.normalOperationMode) {
        normalized.normalOperationMode.pipeline = convertStringEnumToNumber(
            normalized.normalOperationMode.pipeline,
            'Pipeline'
        );
        normalized.normalOperationMode.region = convertStringEnumToNumber(
            normalized.normalOperationMode.region,
            'Region'
        );
        normalized.normalOperationMode.worklane = convertStringEnumToNumber(
            normalized.normalOperationMode.worklane,
            'WorkLane'
        );

        // Normalize channels
        if (normalized.normalOperationMode.channels) {
            normalized.normalOperationMode.channels =
                normalized.normalOperationMode.channels.map((channel: any) => ({
                    ...channel,
                    pipeline: convertStringEnumToNumber(
                        channel.pipeline,
                        'Pipeline'
                    ),
                    region: convertStringEnumToNumber(channel.region, 'Region'),
                    workLane: convertStringEnumToNumber(
                        channel.workLane,
                        'WorkLane'
                    ),
                }));
        }

        // Normalize data channel overrides
        if (normalized.normalOperationMode.dataChannelOverrides) {
            normalized.normalOperationMode.dataChannelOverrides =
                normalized.normalOperationMode.dataChannelOverrides.map(
                    (override: any) => ({
                        ...override,
                        pipeline: convertStringEnumToNumber(
                            override.pipeline,
                            'Pipeline'
                        ),
                        region: convertStringEnumToNumber(
                            override.region,
                            'Region'
                        ),
                        workLane: convertStringEnumToNumber(
                            override.workLane,
                            'WorkLane'
                        ),
                        pipelineSourceType: convertStringEnumToNumber(
                            override.pipelineSourceType,
                            'ReleasePipelineSource'
                        ),
                    })
                );
        }

        // Normalize feature flag overrides
        if (normalized.normalOperationMode.featureFlagsOverrides) {
            normalized.normalOperationMode.featureFlagsOverrides =
                normalized.normalOperationMode.featureFlagsOverrides.map(
                    (override: any) => ({
                        ...override,
                        pipelineSourceType: convertStringEnumToNumber(
                            override.pipelineSourceType,
                            'ReleasePipelineSource'
                        ),
                    })
                );
        }
    }

    // Normalize operation modes
    if (normalized.operationModes) {
        normalized.operationModes = normalized.operationModes.map(
            (mode: any) => {
                const normalizedMode = { ...mode };
                normalizedMode.pipeline = convertStringEnumToNumber(
                    mode.pipeline,
                    'Pipeline'
                );
                normalizedMode.region = convertStringEnumToNumber(
                    mode.region,
                    'Region'
                );
                normalizedMode.worklane = convertStringEnumToNumber(
                    mode.worklane,
                    'WorkLane'
                );

                if (normalizedMode.channels) {
                    normalizedMode.channels = normalizedMode.channels.map(
                        (channel: any) => ({
                            ...channel,
                            pipeline: convertStringEnumToNumber(
                                channel.pipeline,
                                'Pipeline'
                            ),
                            region: convertStringEnumToNumber(
                                channel.region,
                                'Region'
                            ),
                            workLane: convertStringEnumToNumber(
                                channel.workLane,
                                'WorkLane'
                            ),
                        })
                    );
                }

                if (normalizedMode.dataChannelOverrides) {
                    normalizedMode.dataChannelOverrides =
                        normalizedMode.dataChannelOverrides.map(
                            (override: any) => ({
                                ...override,
                                pipeline: convertStringEnumToNumber(
                                    override.pipeline,
                                    'Pipeline'
                                ),
                                region: convertStringEnumToNumber(
                                    override.region,
                                    'Region'
                                ),
                                workLane: convertStringEnumToNumber(
                                    override.workLane,
                                    'WorkLane'
                                ),
                                pipelineSourceType: convertStringEnumToNumber(
                                    override.pipelineSourceType,
                                    'ReleasePipelineSource'
                                ),
                            })
                        );
                }

                if (normalizedMode.featureFlagsOverrides) {
                    normalizedMode.featureFlagsOverrides =
                        normalizedMode.featureFlagsOverrides.map(
                            (override: any) => ({
                                ...override,
                                pipelineSourceType: convertStringEnumToNumber(
                                    override.pipelineSourceType,
                                    'ReleasePipelineSource'
                                ),
                            })
                        );
                }

                return normalizedMode;
            }
        );
    }

    return normalized;
};

export const EnvironmentDataPage: React.FC = () => {
    const themeContext = useContext(ThemeContext);
    const theme = getThemeFromString(themeContext.themeName);
    const placeholderStyles = {
        field: {
            selectors: {
                '::placeholder': {
                    color: theme.palette.neutralQuaternary,
                    opacity: 0.7,
                    fontStyle: 'italic',
                },
            },
        },
    };
    const isMobile = useIsMobile();
    const [environmentList, setEnvironmentList] = useState<string[]>([]);
    const [isLoadingEnvironments, setIsLoadingEnvironments] =
        useState<boolean>(false);
    const [selectedEnvironmentName, setSelectedEnvironmentName] = useState<
        string | null
    >(null);
    const [environmentData, setEnvironmentData] =
        useState<EnvironmentData | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
    const [expandedSections, setExpandedSections] = useState<{
        normal: boolean;
        operations: boolean;
        overrides: boolean;
        flagOverrides: boolean;
        channels: boolean;
        activePreview: boolean;
    }>({
        normal: true,
        operations: false,
        overrides: true,
        flagOverrides: true,
        channels: false,
        activePreview: true,
    });
    const [showOperationModeDialog, setShowOperationModeDialog] =
        useState(false);
    const [pendingOperationMode, setPendingOperationMode] = useState<
        string | null
    >(null);
    const [changedOverrideIndices, setChangedOverrideIndices] = useState<
        Set<number>
    >(new Set());
    const [originalData, setOriginalData] = useState<EnvironmentData | null>(
        null
    );
    const [overrideFilterTypes, setOverrideFilterTypes] = useState<
        Map<number, 'serviceTree' | 'buildPipeline'>
    >(new Map());
    const [collapsedOverrides, setCollapsedOverrides] = useState<Set<number>>(
        new Set()
    );
    const [searchText, setSearchText] = useState<string>('');
    const [filterPipeline, setFilterPipeline] = useState<number | null>(null);
    const [filterRegion, setFilterRegion] = useState<number | null>(null);
    const [filterWorkLane, setFilterWorkLane] = useState<number | null>(null);
    const [filterEnabled, setFilterEnabled] = useState<boolean | null>(null);

    // State for Feature Flag Overrides
    const [collapsedFlagOverrides, setCollapsedFlagOverrides] = useState<
        Set<number>
    >(new Set());
    const [flagOverrideFilterTypes, setFlagOverrideFilterTypes] = useState<
        Map<number, 'serviceTree' | 'buildPipeline'>
    >(new Map());
    const [changedFlagOverrideIndices, setChangedFlagOverrideIndices] =
        useState<Set<number>>(new Set());
    const [flagSearchText, setFlagSearchText] = useState<string>('');
    const [flagFilterEnabled, setFlagFilterEnabled] = useState<boolean | null>(
        null
    );

    // State for Add From URL modal
    const [showAddFromUrlDialog, setShowAddFromUrlDialog] = useState(false);
    const [urlDialogSource, setUrlDialogSource] = useState<
        'dataChannel' | 'featureFlag'
    >('dataChannel');
    const [urlInput, setUrlInput] = useState<string>('');
    const [urlBranchInput, setUrlBranchInput] = useState<string>('');
    const [parsedUrlInfo, setParsedUrlInfo] =
        useState<ParsedPipelineInfo | null>(null);
    const [urlParseError, setUrlParseError] = useState<string | null>(null);

    // State for Add From Clipboard modal
    const [showAddFromClipboardDialog, setShowAddFromClipboardDialog] =
        useState(false);
    const [clipboardInput, setClipboardInput] = useState<string>('');
    const [decodedClipboardData, setDecodedClipboardData] =
        useState<DataChannelOverride | null>(null);
    const [clipboardDecodeError, setClipboardDecodeError] = useState<
        string | null
    >(null);

    // State for Flag Override Add From Clipboard modal
    const [showFlagClipboardDialog, setShowFlagClipboardDialog] =
        useState(false);
    const [flagClipboardInput, setFlagClipboardInput] = useState<string>('');
    const [decodedFlagClipboardData, setDecodedFlagClipboardData] =
        useState<FlagOverride | null>(null);
    const [flagClipboardDecodeError, setFlagClipboardDecodeError] = useState<
        string | null
    >(null);

    // State for CSV import dialogs
    const [showFlagCsvImportDialog, setShowFlagCsvImportDialog] =
        useState(false);
    const [showChannelCsvImportDialog, setShowChannelCsvImportDialog] =
        useState(false);

    // State for unsaved changes confirmation
    const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] =
        useState(false);
    const [pendingEnvironmentChange, setPendingEnvironmentChange] = useState<
        string | null
    >(null);

    // Helper to infer filter type from override data
    const inferFilterType = (override: {
        serviceTreeId?: string | null;
        accountName?: string | null;
        projectName?: string | null;
        branchName?: string | null;
        releaseDefinitionId?: string | null;
    }): 'serviceTree' | 'buildPipeline' => {
        if (override.serviceTreeId && override.serviceTreeId.trim() !== '') {
            return 'serviceTree';
        }
        const hasAccount =
            override.accountName && override.accountName.trim() !== '';
        const hasProject =
            override.projectName && override.projectName.trim() !== '';
        const hasBranch =
            override.branchName && override.branchName.trim() !== '';
        const hasReleaseId =
            override.releaseDefinitionId &&
            override.releaseDefinitionId.trim() !== '';
        if (hasAccount || hasProject || hasBranch || hasReleaseId) {
            return 'buildPipeline';
        }
        return 'serviceTree';
    };

    // Load environment list on mount
    useEffect(() => {
        const loadEnvironments = async () => {
            setIsLoadingEnvironments(true);
            try {
                const environments = await getEnvironmentList();
                setEnvironmentList(environments);
            } catch (error) {
                console.error('Failed to load environments:', error);
                // Error handling and fallback is done in the service
            } finally {
                setIsLoadingEnvironments(false);
            }
        };
        loadEnvironments();
    }, []);

    // Auto-select first environment on mount
    useEffect(() => {
        if (environmentList.length > 0 && !selectedEnvironmentName) {
            setSelectedEnvironmentName(environmentList[0]);
        }
    }, [environmentList, selectedEnvironmentName]);

    // Load environment data when selection changes
    useEffect(() => {
        if (selectedEnvironmentName) {
            loadEnvironmentData(selectedEnvironmentName);
        }
    }, [selectedEnvironmentName]);

    // Track initial data for change detection
    useEffect(() => {
        if (!originalData && environmentData) {
            setOriginalData(JSON.parse(JSON.stringify(environmentData)));
        }
    }, [environmentData, originalData]);

    const loadEnvironmentData = async (envName: string) => {
        setIsLoading(true);
        setErrorMessage(null);
        setSuccessMessage(null);
        setEnvironmentData(null);

        try {
            const data = await fetchEnvironmentData(envName);
            if (!data) {
                setErrorMessage(
                    `No environment data available for "${envName}". Please configure this environment first or contact your administrator.`
                );
                setIsLoading(false);
                return;
            }

            // Convert string enum values to numbers for dropdown compatibility
            const normalizedData = normalizeEnumValues(data);

            setEnvironmentData(normalizedData);
            setOriginalData(JSON.parse(JSON.stringify(normalizedData)));
            setChangedOverrideIndices(new Set());

            // Initialize collapsed state for all overrides (all collapsed by default)
            const collapsedOverridesSet = new Set<number>();
            if (normalizedData.normalOperationMode?.dataChannelOverrides) {
                normalizedData.normalOperationMode.dataChannelOverrides.forEach(
                    (_, index) => {
                        collapsedOverridesSet.add(index);
                    }
                );
            }
            setCollapsedOverrides(collapsedOverridesSet);

            // Initialize collapsed state for all flag overrides (all collapsed by default)
            const collapsedFlagSet = new Set<number>();
            if (normalizedData.normalOperationMode?.featureFlagsOverrides) {
                normalizedData.normalOperationMode.featureFlagsOverrides.forEach(
                    (_, index) => {
                        collapsedFlagSet.add(index);
                    }
                );
            }
            setCollapsedFlagOverrides(collapsedFlagSet);

            // Infer filter types from existing data
            const filterTypes = new Map<
                number,
                'serviceTree' | 'buildPipeline'
            >();
            if (normalizedData.normalOperationMode?.dataChannelOverrides) {
                normalizedData.normalOperationMode.dataChannelOverrides.forEach(
                    (override, index) => {
                        filterTypes.set(index, inferFilterType(override));
                    }
                );
            }
            setOverrideFilterTypes(filterTypes);

            // Infer filter types for flag overrides
            const flagFilterTypes = new Map<
                number,
                'serviceTree' | 'buildPipeline'
            >();
            if (normalizedData.normalOperationMode?.featureFlagsOverrides) {
                normalizedData.normalOperationMode.featureFlagsOverrides.forEach(
                    (override, index) => {
                        flagFilterTypes.set(index, inferFilterType(override));
                    }
                );
            }
            setFlagOverrideFilterTypes(flagFilterTypes);
        } catch (error) {
            console.error('Error loading environment data:', error);
            setErrorMessage(
                (error as Error).message || 'Failed to load environment data'
            );
        } finally {
            setIsLoading(false);
        }
    };

    const handleSave = async () => {
        if (!environmentData) return;

        setIsSaving(true);
        setErrorMessage(null);
        setSuccessMessage(null);

        try {
            const result = await saveEnvironmentData(environmentData);

            if (result.success) {
                setSuccessMessage(
                    result.message || 'Environment data saved successfully'
                );
                setHasUnsavedChanges(false);
                if (result.data) {
                    // Normalize the returned data to convert string enums to numbers
                    const normalizedData = normalizeEnumValues(result.data);
                    setEnvironmentData(normalizedData);
                    setOriginalData(JSON.parse(JSON.stringify(normalizedData)));
                    setChangedOverrideIndices(new Set());

                    // Initialize collapsed state for all overrides
                    const collapsedOverridesSet = new Set<number>();
                    if (
                        normalizedData.normalOperationMode?.dataChannelOverrides
                    ) {
                        normalizedData.normalOperationMode.dataChannelOverrides.forEach(
                            (_, index) => {
                                collapsedOverridesSet.add(index);
                            }
                        );
                    }
                    setCollapsedOverrides(collapsedOverridesSet);

                    // Initialize collapsed state for all flag overrides
                    const collapsedFlagSet = new Set<number>();
                    if (
                        normalizedData.normalOperationMode
                            ?.featureFlagsOverrides
                    ) {
                        normalizedData.normalOperationMode.featureFlagsOverrides.forEach(
                            (_, index) => {
                                collapsedFlagSet.add(index);
                            }
                        );
                    }
                    setCollapsedFlagOverrides(collapsedFlagSet);
                    setChangedFlagOverrideIndices(new Set());
                }
            } else {
                setErrorMessage(
                    result.message || 'Failed to save environment data'
                );
            }
        } catch (error) {
            console.error('Error saving environment data:', error);
            setErrorMessage(
                (error as Error).message || 'Failed to save environment data'
            );
        } finally {
            setIsSaving(false);
        }
    };

    const handleReload = () => {
        if (selectedEnvironmentName) {
            loadEnvironmentData(selectedEnvironmentName);
            setHasUnsavedChanges(false);
            setErrorMessage(null);
            setSuccessMessage(null);
        }
    };

    const toggleSection = (section: keyof typeof expandedSections) => {
        setExpandedSections({
            ...expandedSections,
            [section]: !expandedSections[section],
        });
    };

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text).then(
            () => {
                // Success
            },
            (err) => {
                console.error('Failed to copy: ', err);
            }
        );
    };

    const copyOverride = (override: DataChannelOverride) => {
        const overrideText = JSON.stringify(override, null, 2);
        copyToClipboard(overrideText);
    };

    const isOverrideChanged = (index: number): boolean => {
        if (!originalData || !environmentData) return true;
        const current =
            environmentData.normalOperationMode.dataChannelOverrides[index];
        const currentJson = JSON.stringify(current);
        const originals = originalData.normalOperationMode.dataChannelOverrides;
        // Check if this exact item exists anywhere in the original array
        return !originals.some((orig) => JSON.stringify(orig) === currentJson);
    };

    const updateFeatureFlag = (
        modeType: 'normal' | 'operation',
        modeIndex: number | null,
        flagKey: keyof EnvironmentFlags,
        value: boolean | null
    ) => {
        if (!environmentData) return;
        const newData = { ...environmentData };
        if (modeType === 'normal') {
            newData.normalOperationMode.featureFlags[flagKey] = value;
        } else if (modeIndex !== null && modeIndex >= 0) {
            newData.operationModes[modeIndex].featureFlags[flagKey] = value;
        }
        setEnvironmentData(newData);
        setHasUnsavedChanges(true);
    };

    const handleAddOverride = (
        override?: DataChannelOverride,
        filterType?: 'serviceTree' | 'buildPipeline'
    ) => {
        if (!environmentData) return;
        const newOverride: DataChannelOverride = override || {
            name: 'New Override',
            enabled: true,
            active: false,
            workLane: null,
            pipeline: null,
            region: null,
            serviceTreeId: null,
            accountName: null,
            projectName: null,
            branchName: null,
            releaseDefinitionId: null,
            pipelineSourceType: null,
            tags: [],
            startTime: null,
            endTime: null,
        };

        const newData = { ...environmentData };
        newData.normalOperationMode.dataChannelOverrides.unshift(newOverride);
        setEnvironmentData(newData);
        setHasUnsavedChanges(true);

        // Set filter type for new override
        const newFilterTypes = new Map(overrideFilterTypes);
        const typeToSet = filterType || 'serviceTree';
        newFilterTypes.set(0, typeToSet);
        setOverrideFilterTypes(newFilterTypes);

        // Update indices for existing overrides
        const updatedFilterTypes = new Map<
            number,
            'serviceTree' | 'buildPipeline'
        >();
        newFilterTypes.forEach((type, oldIndex) => {
            updatedFilterTypes.set(oldIndex + 1, type);
        });
        updatedFilterTypes.set(0, typeToSet);
        setOverrideFilterTypes(updatedFilterTypes);

        // Don't collapse the new override, but shift indices for collapsed overrides
        const updatedCollapsed = new Set<number>();
        collapsedOverrides.forEach((index) => {
            updatedCollapsed.add(index + 1);
        });
        setCollapsedOverrides(updatedCollapsed);
    };

    const handleRemoveOverride = (index: number) => {
        if (!environmentData) return;
        const newData = { ...environmentData };
        newData.normalOperationMode.dataChannelOverrides.splice(index, 1);
        setEnvironmentData(newData);
        setHasUnsavedChanges(true);

        // Update collapsed state - remove this index and shift down indices above it
        const updatedCollapsed = new Set<number>();
        collapsedOverrides.forEach((collapsedIndex) => {
            if (collapsedIndex < index) {
                updatedCollapsed.add(collapsedIndex);
            } else if (collapsedIndex > index) {
                updatedCollapsed.add(collapsedIndex - 1);
            }
        });
        setCollapsedOverrides(updatedCollapsed);
    };

    const toggleOverrideCollapse = (index: number) => {
        const newCollapsed = new Set(collapsedOverrides);
        if (newCollapsed.has(index)) {
            newCollapsed.delete(index);
        } else {
            newCollapsed.add(index);
        }
        setCollapsedOverrides(newCollapsed);
    };

    const clearFilters = () => {
        setSearchText('');
        setFilterPipeline(null);
        setFilterRegion(null);
        setFilterWorkLane(null);
        setFilterEnabled(null);
    };

    const filterOverrides = (
        overrides: DataChannelOverride[]
    ): Array<{ override: DataChannelOverride; originalIndex: number }> => {
        const filtered: Array<{
            override: DataChannelOverride;
            originalIndex: number;
        }> = [];
        overrides.forEach((override, originalIndex) => {
            // Text search (OR operation across multiple fields including tags)
            if (searchText.trim() !== '') {
                const searchLower = searchText.toLowerCase();
                const matchesSearch =
                    (override.name &&
                        override.name.toLowerCase().includes(searchLower)) ||
                    (override.serviceTreeId &&
                        override.serviceTreeId
                            .toLowerCase()
                            .includes(searchLower)) ||
                    (override.accountName &&
                        override.accountName
                            .toLowerCase()
                            .includes(searchLower)) ||
                    (override.projectName &&
                        override.projectName
                            .toLowerCase()
                            .includes(searchLower)) ||
                    (override.branchName &&
                        override.branchName
                            .toLowerCase()
                            .includes(searchLower)) ||
                    (override.releaseDefinitionId &&
                        override.releaseDefinitionId
                            .toLowerCase()
                            .includes(searchLower)) ||
                    (override.tags &&
                        override.tags.some((tag) =>
                            tag.toLowerCase().includes(searchLower)
                        ));

                if (!matchesSearch) return;
            }

            // Filter dropdowns (AND operation)
            if (filterPipeline !== null && override.pipeline !== filterPipeline)
                return;
            if (filterRegion !== null && override.region !== filterRegion)
                return;
            if (filterWorkLane !== null && override.workLane !== filterWorkLane)
                return;
            if (filterEnabled !== null && override.enabled !== filterEnabled)
                return;

            filtered.push({ override, originalIndex });
        });
        return filtered;
    };

    const updateOverrideField = (
        index: number,
        field: keyof DataChannelOverride,
        value: any
    ) => {
        if (!environmentData) return;
        const newData = { ...environmentData };
        const override: Record<string, any> = {
            ...newData.normalOperationMode.dataChannelOverrides[index],
        };
        override[field] = value;
        newData.normalOperationMode.dataChannelOverrides[index] =
            override as DataChannelOverride;

        const newChanged = new Set(changedOverrideIndices);
        newChanged.add(index);
        setChangedOverrideIndices(newChanged);

        setEnvironmentData(newData);
        setHasUnsavedChanges(true);
    };

    // === Feature Flag Override Handlers ===

    const handleAddFlagOverride = (
        override?: FlagOverride,
        filterType?: 'serviceTree' | 'buildPipeline'
    ) => {
        if (!environmentData) return;
        const defaultFlags: EnvironmentFlags = {
            useDataChannelOverrides: null,
            cachingEnabled: null,
            messagingEnabled: null,
            storageUploadEnabled: null,
            octopusEnabled: null,
            lockBoxEnabled: null,
            egressEnabled: null,
            icmEnabled: null,
            requiredOnNetworkShare: null,
            useDynamicHealthChecks: null,
            useSrxV2: null,
            blockSrxExecution: null,
            messagingHeartbeatEnabled: null,
        };
        const newOverride: FlagOverride = override || {
            name: 'New Flag Override',
            enabled: true,
            active: false,
            serviceTreeId: null,
            accountName: null,
            projectName: null,
            branchName: null,
            releaseDefinitionId: null,
            pipelineSourceType: null,
            tags: [],
            startTime: null,
            endTime: null,
            featureFlags: { ...defaultFlags },
        };
        // Merge default flags with existing featureFlags to ensure all keys are present
        newOverride.featureFlags = {
            ...defaultFlags,
            ...(newOverride.featureFlags ?? {}),
        };

        const newData = { ...environmentData };
        if (!newData.normalOperationMode.featureFlagsOverrides) {
            newData.normalOperationMode.featureFlagsOverrides = [];
        }
        newData.normalOperationMode.featureFlagsOverrides.unshift(newOverride);
        setEnvironmentData(newData);
        setHasUnsavedChanges(true);

        // Set filter type for new override
        const typeToSet = filterType || 'serviceTree';
        const updatedFilterTypes = new Map<
            number,
            'serviceTree' | 'buildPipeline'
        >();
        flagOverrideFilterTypes.forEach((type, oldIndex) => {
            updatedFilterTypes.set(oldIndex + 1, type);
        });
        updatedFilterTypes.set(0, typeToSet);
        setFlagOverrideFilterTypes(updatedFilterTypes);

        // Don't collapse the new override, but shift indices for collapsed overrides
        const updatedCollapsed = new Set<number>();
        collapsedFlagOverrides.forEach((index) => {
            updatedCollapsed.add(index + 1);
        });
        setCollapsedFlagOverrides(updatedCollapsed);
    };

    const handleRemoveFlagOverride = (index: number) => {
        if (!environmentData) return;
        const newData = { ...environmentData };
        if (!newData.normalOperationMode.featureFlagsOverrides) return;
        newData.normalOperationMode.featureFlagsOverrides.splice(index, 1);
        setEnvironmentData(newData);
        setHasUnsavedChanges(true);

        const updatedCollapsed = new Set<number>();
        collapsedFlagOverrides.forEach((collapsedIndex) => {
            if (collapsedIndex < index) {
                updatedCollapsed.add(collapsedIndex);
            } else if (collapsedIndex > index) {
                updatedCollapsed.add(collapsedIndex - 1);
            }
        });
        setCollapsedFlagOverrides(updatedCollapsed);
    };

    const toggleFlagOverrideCollapse = (index: number) => {
        const newCollapsed = new Set(collapsedFlagOverrides);
        if (newCollapsed.has(index)) {
            newCollapsed.delete(index);
        } else {
            newCollapsed.add(index);
        }
        setCollapsedFlagOverrides(newCollapsed);
    };

    const clearFlagFilters = () => {
        setFlagSearchText('');
        setFlagFilterEnabled(null);
    };

    const filterFlagOverrides = (
        overrides: FlagOverride[]
    ): Array<{ override: FlagOverride; originalIndex: number }> => {
        const filtered: Array<{
            override: FlagOverride;
            originalIndex: number;
        }> = [];
        overrides.forEach((override, originalIndex) => {
            // Text search
            if (flagSearchText.trim() !== '') {
                const searchLower = flagSearchText.toLowerCase();
                const matchesSearch =
                    (override.name &&
                        override.name.toLowerCase().includes(searchLower)) ||
                    (override.serviceTreeId &&
                        override.serviceTreeId
                            .toLowerCase()
                            .includes(searchLower)) ||
                    (override.accountName &&
                        override.accountName
                            .toLowerCase()
                            .includes(searchLower)) ||
                    (override.projectName &&
                        override.projectName
                            .toLowerCase()
                            .includes(searchLower)) ||
                    (override.branchName &&
                        override.branchName
                            .toLowerCase()
                            .includes(searchLower)) ||
                    (override.releaseDefinitionId &&
                        override.releaseDefinitionId
                            .toLowerCase()
                            .includes(searchLower)) ||
                    (override.tags &&
                        override.tags.some((tag) =>
                            tag.toLowerCase().includes(searchLower)
                        ));

                if (!matchesSearch) return;
            }

            // Filter by enabled
            if (
                flagFilterEnabled !== null &&
                override.enabled !== flagFilterEnabled
            )
                return;

            filtered.push({ override, originalIndex });
        });
        return filtered;
    };

    const updateFlagOverrideField = <K extends keyof FlagOverride>(
        index: number,
        field: K,
        value: FlagOverride[K]
    ) => {
        if (!environmentData) return;
        const newData = { ...environmentData };
        if (!newData.normalOperationMode.featureFlagsOverrides) return;
        const override = {
            ...newData.normalOperationMode.featureFlagsOverrides[index],
        };
        override[field] = value;
        newData.normalOperationMode.featureFlagsOverrides[index] = override;

        const newChanged = new Set(changedFlagOverrideIndices);
        newChanged.add(index);
        setChangedFlagOverrideIndices(newChanged);

        setEnvironmentData(newData);
        setHasUnsavedChanges(true);
    };

    const updateFlagOverrideFeatureFlag = (
        index: number,
        flagKey: keyof EnvironmentFlags,
        value: boolean | null
    ) => {
        if (!environmentData) return;
        const newData = { ...environmentData };
        if (!newData.normalOperationMode.featureFlagsOverrides) return;
        const override = {
            ...newData.normalOperationMode.featureFlagsOverrides[index],
        };
        const flags = override.featureFlags ?? {};
        override.featureFlags = { ...flags, [flagKey]: value };
        newData.normalOperationMode.featureFlagsOverrides[index] = override;

        const newChanged = new Set(changedFlagOverrideIndices);
        newChanged.add(index);
        setChangedFlagOverrideIndices(newChanged);

        setEnvironmentData(newData);
        setHasUnsavedChanges(true);
    };

    const isFlagOverrideChanged = (index: number): boolean => {
        if (
            !originalData ||
            !originalData.normalOperationMode.featureFlagsOverrides
        )
            return true;
        if (
            !environmentData ||
            !environmentData.normalOperationMode.featureFlagsOverrides
        )
            return true;
        const current =
            environmentData.normalOperationMode.featureFlagsOverrides[index];
        const currentJson = JSON.stringify(current);
        const originals =
            originalData.normalOperationMode.featureFlagsOverrides;
        // Check if this exact item exists anywhere in the original array
        return !originals.some((orig) => JSON.stringify(orig) === currentJson);
    };

    const copyFlagOverride = (override: FlagOverride) => {
        const overrideText = JSON.stringify(override, null, 2);
        copyToClipboard(overrideText);
    };

    const copyFlagFilterToClipboard = (override: FlagOverride) => {
        const encoded = encodeFlagOverrideToClipboard(override);
        navigator.clipboard.writeText(encoded).then(
            () => {
                setSuccessMessage(
                    `Flag override "${override.name}" copied to clipboard`
                );
            },
            (err) => {
                console.error(
                    'Failed to copy flag override to clipboard:',
                    err
                );
                setErrorMessage('Failed to copy flag override to clipboard');
            }
        );
    };

    const isFlagOverrideActive = (override: FlagOverride): boolean => {
        return (
            override.enabled &&
            (override.startTime == null ||
                new Date() > new Date(override.startTime)) &&
            (override.endTime == null ||
                new Date(override.endTime) > new Date())
        );
    };

    const handleFlagFilterTypeChange = (
        index: number,
        newType: 'serviceTree' | 'buildPipeline'
    ) => {
        const newFilterTypes = new Map(flagOverrideFilterTypes);
        newFilterTypes.set(index, newType);
        setFlagOverrideFilterTypes(newFilterTypes);
    };

    // === CSV Export/Import Handlers ===

    const handleExportFlagOverridesCsv = () => {
        if (!environmentData) return;
        const overrides =
            environmentData.normalOperationMode.featureFlagsOverrides || [];
        if (overrides.length === 0) return;
        const csv = exportFlagOverridesToCsv(overrides);
        const filename = generateCsvFilename('FeatureFlagOverrides');
        downloadCsv(csv, filename);
        setSuccessMessage(
            `Exported ${overrides.length} feature flag override${
                overrides.length !== 1 ? 's' : ''
            } to ${filename}`
        );
    };

    const handleImportFlagOverridesCsv = (
        imported: FlagOverride[],
        mode: 'append' | 'replace'
    ) => {
        if (!environmentData) return;
        const newData = { ...environmentData };
        if (!newData.normalOperationMode.featureFlagsOverrides) {
            newData.normalOperationMode.featureFlagsOverrides = [];
        }

        if (mode === 'replace') {
            newData.normalOperationMode.featureFlagsOverrides = [...imported];
        } else {
            newData.normalOperationMode.featureFlagsOverrides = [
                ...imported,
                ...newData.normalOperationMode.featureFlagsOverrides,
            ];
        }

        setEnvironmentData(newData);
        setHasUnsavedChanges(true);

        // Rebuild collapsed state — collapse all
        const collapsed = new Set<number>();
        newData.normalOperationMode.featureFlagsOverrides.forEach(
            (_, index) => {
                collapsed.add(index);
            }
        );
        setCollapsedFlagOverrides(collapsed);

        // Rebuild filter types
        const filterTypes = new Map<number, 'serviceTree' | 'buildPipeline'>();
        newData.normalOperationMode.featureFlagsOverrides.forEach(
            (override, index) => {
                filterTypes.set(index, inferFilterType(override));
            }
        );
        setFlagOverrideFilterTypes(filterTypes);

        setShowFlagCsvImportDialog(false);
        setSuccessMessage(
            `${mode === 'replace' ? 'Replaced with' : 'Imported'} ${
                imported.length
            } feature flag override${imported.length !== 1 ? 's' : ''}`
        );
    };

    const handleExportChannelOverridesCsv = () => {
        if (!environmentData) return;
        const overrides =
            environmentData.normalOperationMode.dataChannelOverrides || [];
        if (overrides.length === 0) return;
        const csv = exportDataChannelOverridesToCsv(overrides);
        const filename = generateCsvFilename('DataChannelOverrides');
        downloadCsv(csv, filename);
        setSuccessMessage(
            `Exported ${overrides.length} data channel override${
                overrides.length !== 1 ? 's' : ''
            } to ${filename}`
        );
    };

    const handleImportChannelOverridesCsv = (
        imported: DataChannelOverride[],
        mode: 'append' | 'replace'
    ) => {
        if (!environmentData) return;
        const newData = { ...environmentData };

        if (mode === 'replace') {
            newData.normalOperationMode.dataChannelOverrides = [...imported];
        } else {
            newData.normalOperationMode.dataChannelOverrides = [
                ...imported,
                ...newData.normalOperationMode.dataChannelOverrides,
            ];
        }

        setEnvironmentData(newData);
        setHasUnsavedChanges(true);

        // Rebuild collapsed state — collapse all
        const collapsed = new Set<number>();
        newData.normalOperationMode.dataChannelOverrides.forEach((_, index) => {
            collapsed.add(index);
        });
        setCollapsedOverrides(collapsed);

        // Rebuild filter types
        const filterTypes = new Map<number, 'serviceTree' | 'buildPipeline'>();
        newData.normalOperationMode.dataChannelOverrides.forEach(
            (override, index) => {
                filterTypes.set(index, inferFilterType(override));
            }
        );
        setOverrideFilterTypes(filterTypes);

        setShowChannelCsvImportDialog(false);
        setSuccessMessage(
            `${mode === 'replace' ? 'Replaced with' : 'Imported'} ${
                imported.length
            } data channel override${imported.length !== 1 ? 's' : ''}`
        );
    };

    const handleOperationModeChange = (newMode: string) => {
        if (!environmentData) return;
        if (newMode !== environmentData.activeOperationalModeName) {
            setPendingOperationMode(newMode);
            setShowOperationModeDialog(true);
        }
    };

    const confirmOperationModeChange = () => {
        if (!environmentData || !pendingOperationMode) return;
        setEnvironmentData({
            ...environmentData,
            activeOperationalModeName: pendingOperationMode,
        });
        setHasUnsavedChanges(true);
        setShowOperationModeDialog(false);
        setPendingOperationMode(null);
    };

    const cancelOperationModeChange = () => {
        setShowOperationModeDialog(false);
        setPendingOperationMode(null);
    };

    // Handle URL input change with real-time parsing
    const handleUrlInputChange = (newUrl: string) => {
        setUrlInput(newUrl);
        if (newUrl.trim() === '') {
            setParsedUrlInfo(null);
            setUrlParseError(null);
            return;
        }

        const parsed = parseAzureDevOpsUrl(newUrl);
        if (parsed) {
            setParsedUrlInfo(parsed);
            setUrlParseError(null);
        } else {
            setParsedUrlInfo(null);
            setUrlParseError(
                'Invalid Azure DevOps URL. Please enter a valid build or release URL.'
            );
        }
    };

    // Confirm Add From URL
    const confirmAddFromUrl = () => {
        if (!parsedUrlInfo) {
            setUrlParseError('Please enter a valid URL');
            return;
        }

        const baseName = `${parsedUrlInfo.accountName}/${
            parsedUrlInfo.projectName
        } - ${parsedUrlInfo.pipelineSourceType === 1 ? 'YAML' : 'Classic'}`;
        const baseFields = {
            name: baseName,
            enabled: true,
            active: false,
            serviceTreeId: null,
            accountName: parsedUrlInfo.accountName,
            projectName: parsedUrlInfo.projectName,
            branchName: urlBranchInput.trim() || null,
            releaseDefinitionId: parsedUrlInfo.releaseDefinitionId,
            pipelineSourceType: parsedUrlInfo.pipelineSourceType,
            tags: [],
            startTime: null,
            endTime: null,
        };

        if (urlDialogSource === 'featureFlag') {
            const defaultFlags: EnvironmentFlags = {
                useDataChannelOverrides: null,
                cachingEnabled: null,
                messagingEnabled: null,
                storageUploadEnabled: null,
                octopusEnabled: null,
                lockBoxEnabled: null,
                egressEnabled: null,
                icmEnabled: null,
                requiredOnNetworkShare: null,
                useDynamicHealthChecks: null,
                useSrxV2: null,
                blockSrxExecution: null,
                messagingHeartbeatEnabled: null,
            };
            const newFlagOverride: FlagOverride = {
                ...baseFields,
                featureFlags: defaultFlags,
            };
            handleAddFlagOverride(newFlagOverride, 'buildPipeline');
        } else {
            const newOverride: DataChannelOverride = {
                ...baseFields,
                workLane: null,
                pipeline: null,
                region: null,
            };
            handleAddOverride(newOverride, 'buildPipeline');
        }
        setSuccessMessage(
            `Filter added from URL: ${parsedUrlInfo.accountName}/${parsedUrlInfo.projectName}`
        );

        // Reset modal state
        setShowAddFromUrlDialog(false);
        setUrlInput('');
        setUrlBranchInput('');
        setParsedUrlInfo(null);
        setUrlParseError(null);
    };

    // Cancel Add From URL
    const cancelAddFromUrl = () => {
        setShowAddFromUrlDialog(false);
        setUrlInput('');
        setUrlBranchInput('');
        setParsedUrlInfo(null);
        setUrlParseError(null);
    };

    // Handle clipboard input change with real-time decoding
    const handleClipboardInputChange = (newInput: string) => {
        setClipboardInput(newInput);
        if (newInput.trim() === '') {
            setDecodedClipboardData(null);
            setClipboardDecodeError(null);
            return;
        }

        const decoded = decodeFilterFromClipboard(newInput.trim());
        if (decoded) {
            setDecodedClipboardData(decoded);
            setClipboardDecodeError(null);
        } else {
            setDecodedClipboardData(null);
            setClipboardDecodeError(
                'Invalid clipboard data. Please paste valid filter data.'
            );
        }
    };

    // Confirm Add From Clipboard
    const confirmAddFromClipboard = () => {
        if (!decodedClipboardData) {
            setClipboardDecodeError('Please paste valid clipboard data');
            return;
        }

        // Determine filter type from decoded data
        const filterType = inferFilterType(decodedClipboardData);

        handleAddOverride(decodedClipboardData, filterType);
        setSuccessMessage(
            `Filter added from clipboard: ${decodedClipboardData.name}`
        );

        // Reset modal state
        setShowAddFromClipboardDialog(false);
        setClipboardInput('');
        setDecodedClipboardData(null);
        setClipboardDecodeError(null);
    };

    // Cancel Add From Clipboard
    const cancelAddFromClipboard = () => {
        setShowAddFromClipboardDialog(false);
        setClipboardInput('');
        setDecodedClipboardData(null);
        setClipboardDecodeError(null);
    };

    // Handle Flag Override clipboard input change
    const handleFlagClipboardInputChange = (newInput: string) => {
        setFlagClipboardInput(newInput);
        if (newInput.trim() === '') {
            setDecodedFlagClipboardData(null);
            setFlagClipboardDecodeError(null);
            return;
        }

        const decoded = decodeFlagOverrideFromClipboard(newInput.trim());
        if (decoded) {
            setDecodedFlagClipboardData(decoded);
            setFlagClipboardDecodeError(null);
        } else {
            setDecodedFlagClipboardData(null);
            setFlagClipboardDecodeError(
                'Invalid clipboard data. Please paste valid flag override data copied from "Copy to Clipboard".'
            );
        }
    };

    // Confirm add flag override from clipboard
    const confirmFlagClipboard = () => {
        if (!decodedFlagClipboardData) {
            setFlagClipboardDecodeError(
                'Please paste valid flag override clipboard data'
            );
            return;
        }

        // Determine filter type from decoded data
        const filterType = inferFilterType(decodedFlagClipboardData);
        handleAddFlagOverride(decodedFlagClipboardData, filterType);
        setSuccessMessage(
            `Flag override added from clipboard: ${decodedFlagClipboardData.name}`
        );

        // Reset modal state
        setShowFlagClipboardDialog(false);
        setFlagClipboardInput('');
        setDecodedFlagClipboardData(null);
        setFlagClipboardDecodeError(null);
    };

    // Cancel flag clipboard dialog
    const cancelFlagClipboard = () => {
        setShowFlagClipboardDialog(false);
        setFlagClipboardInput('');
        setDecodedFlagClipboardData(null);
        setFlagClipboardDecodeError(null);
    };

    // Copy filter to clipboard
    const copyFilterToClipboard = (override: DataChannelOverride) => {
        const encoded = encodeFilterToClipboard(override);
        navigator.clipboard.writeText(encoded).then(
            () => {
                setSuccessMessage(
                    `Filter "${override.name}" copied to clipboard`
                );
            },
            (err) => {
                console.error('Failed to copy filter to clipboard:', err);
                setErrorMessage('Failed to copy filter to clipboard');
            }
        );
    };

    // Compute active status based on backend IsActive() logic
    const isOverrideActive = (override: DataChannelOverride): boolean => {
        return (
            override.enabled &&
            (override.startTime == null ||
                new Date() > new Date(override.startTime)) &&
            (override.endTime == null ||
                new Date(override.endTime) > new Date())
        );
    };

    // Update handler for normal operation mode fields
    const updateNormalModeField = (
        field: 'isEnabled' | 'region' | 'pipeline' | 'worklane',
        value: any
    ) => {
        if (!environmentData) return;
        const newData = { ...environmentData };
        const mode: Record<string, any> = { ...newData.normalOperationMode };
        mode[field] = value;
        newData.normalOperationMode =
            mode as typeof newData.normalOperationMode;
        setEnvironmentData(newData);
        setHasUnsavedChanges(true);
    };

    // Update handler for operation mode fields
    const updateOperationModeField = (
        modeIndex: number,
        field: 'isEnabled' | 'region' | 'pipeline' | 'worklane',
        value: any
    ) => {
        if (!environmentData) return;
        const newData = { ...environmentData };
        const opMode: Record<string, any> = {
            ...newData.operationModes[modeIndex],
        };
        opMode[field] = value;
        newData.operationModes[modeIndex] =
            opMode as typeof newData.operationModes[number];
        setEnvironmentData(newData);
        setHasUnsavedChanges(true);
    };

    const handleFilterTypeChange = (
        index: number,
        newType: 'serviceTree' | 'buildPipeline'
    ) => {
        const newFilterTypes = new Map(overrideFilterTypes);
        newFilterTypes.set(index, newType);
        setOverrideFilterTypes(newFilterTypes);
    };

    const environmentOptions: IDropdownOption[] = environmentList.map(
        (env) => ({
            key: env,
            text: env,
        })
    );

    const handleEnvironmentChange = (
        event: React.FormEvent<HTMLDivElement>,
        option?: IDropdownOption
    ) => {
        if (option && option.key !== selectedEnvironmentName) {
            // Check for unsaved changes before switching
            if (hasUnsavedChanges) {
                setPendingEnvironmentChange(option.key as string);
                setShowUnsavedChangesDialog(true);
            } else {
                // No unsaved changes, switch immediately
                clearMessagesAndSwitch(option.key as string);
            }
        }
    };

    const clearMessagesAndSwitch = (envName: string) => {
        setSelectedEnvironmentName(envName);
        setErrorMessage(null);
        setSuccessMessage(null);
        setHasUnsavedChanges(false);
    };

    const confirmEnvironmentChange = () => {
        if (pendingEnvironmentChange) {
            clearMessagesAndSwitch(pendingEnvironmentChange);
            setShowUnsavedChangesDialog(false);
            setPendingEnvironmentChange(null);
        }
    };

    const cancelEnvironmentChange = () => {
        setShowUnsavedChangesDialog(false);
        setPendingEnvironmentChange(null);
    };

    return (
        <div
            style={{
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            {/* Main Content Area */}
            <div
                style={{
                    paddingTop: isMobile ? '16px' : '0',
                    maxWidth: '100%',
                }}
            >
                {/* Unified Card Container - Always uses same structure */}
                <div
                    style={{
                        backgroundColor: theme.palette.white,
                        borderRadius: '4px',
                        boxShadow:
                            '0 3.2px 7.2px 0 rgba(0, 0, 0, .132), 0 .6px 1.8px 0 rgba(0, 0, 0, .108)',
                        overflow: 'hidden',
                        marginBottom: '16px',
                    }}
                >
                    {/* Header */}
                    <HubHeader
                        title="Environment Configuration"
                        subtitle={selectedEnvironmentName}
                        actions={
                            <>
                                {!isLoadingEnvironments &&
                                    environmentList.length > 0 && (
                                        <Dropdown
                                            placeholder="Select an environment"
                                            options={environmentOptions}
                                            selectedKey={
                                                selectedEnvironmentName ||
                                                undefined
                                            }
                                            onChange={handleEnvironmentChange}
                                            styles={{
                                                dropdown: {
                                                    width: isMobile
                                                        ? '100%'
                                                        : 250,
                                                },
                                                root: {
                                                    width: isMobile
                                                        ? '100%'
                                                        : 250,
                                                },
                                            }}
                                        />
                                    )}
                                {environmentData && (
                                    <div
                                        style={{
                                            display: 'flex',
                                            gap: '8px',
                                            flexShrink: 0,
                                        }}
                                    >
                                        <DefaultButton
                                            text={isMobile ? '' : 'Reload'}
                                            onClick={handleReload}
                                            iconProps={{ iconName: 'Refresh' }}
                                            title="Reload"
                                        />
                                        <PrimaryButton
                                            text="Save"
                                            onClick={handleSave}
                                            disabled={isSaving}
                                            iconProps={{ iconName: 'Save' }}
                                        />
                                    </div>
                                )}
                            </>
                        }
                    />

                    {/* Content Area - Conditional rendering based on state */}
                    <div style={{ padding: isMobile ? '16px' : '20px' }}>
                        {/* Loading State for initial environment list */}
                        {isLoadingEnvironments && (
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    padding: '40px 0',
                                }}
                            >
                                <Spinner
                                    size={SpinnerSize.large}
                                    label="Loading environment list..."
                                />
                            </div>
                        )}

                        {/* No Environments Message */}
                        {!isLoadingEnvironments &&
                            environmentList.length === 0 && (
                                <MessageBar
                                    messageBarType={MessageBarType.info}
                                    isMultiline={false}
                                >
                                    No environments available
                                </MessageBar>
                            )}

                        {/* Loading Spinner for environment data */}
                        {!isLoadingEnvironments &&
                            isLoading &&
                            selectedEnvironmentName && (
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        padding: '40px 0',
                                    }}
                                >
                                    <Spinner
                                        size={SpinnerSize.large}
                                        label={`Loading ${selectedEnvironmentName}...`}
                                    />
                                </div>
                            )}

                        {/* Environment Data Content */}
                        {!isLoadingEnvironments &&
                            !isLoading &&
                            environmentData && (
                                <>
                                    {errorMessage && (
                                        <MessageBar
                                            messageBarType={
                                                MessageBarType.error
                                            }
                                            isMultiline={true}
                                            onDismiss={() =>
                                                setErrorMessage(null)
                                            }
                                            dismissButtonAriaLabel="Close"
                                            styles={{
                                                root: {
                                                    marginBottom: '16px',
                                                    backgroundColor:
                                                        theme.palette.redDark,
                                                },
                                                text: {
                                                    color: theme.palette.white,
                                                },
                                                icon: {
                                                    color: theme.palette.white,
                                                },
                                                dismissal: {
                                                    color: theme.palette.white,
                                                },
                                            }}
                                        >
                                            {errorMessage}
                                        </MessageBar>
                                    )}
                                    {successMessage && (
                                        <MessageBar
                                            messageBarType={
                                                MessageBarType.success
                                            }
                                            isMultiline={false}
                                            onDismiss={() =>
                                                setSuccessMessage(null)
                                            }
                                            dismissButtonAriaLabel="Close"
                                            styles={{
                                                root: {
                                                    marginBottom: '16px',
                                                    backgroundColor:
                                                        theme.palette.green,
                                                },
                                                text: {
                                                    color: theme.palette.white,
                                                },
                                                icon: {
                                                    color: theme.palette.white,
                                                },
                                                dismissal: {
                                                    color: theme.palette.white,
                                                },
                                            }}
                                        >
                                            {successMessage}
                                        </MessageBar>
                                    )}
                                    {hasUnsavedChanges && (
                                        <MessageBar
                                            messageBarType={MessageBarType.info}
                                            isMultiline={false}
                                            styles={{
                                                root: {
                                                    marginBottom: '16px',
                                                    backgroundColor:
                                                        theme.palette
                                                            .themePrimary,
                                                },
                                                text: {
                                                    color: theme.palette.white,
                                                },
                                                icon: {
                                                    color: theme.palette.white,
                                                },
                                            }}
                                        >
                                            You have unsaved changes
                                        </MessageBar>
                                    )}
                                    <Stack
                                        tokens={{ childrenGap: 16 }}
                                        horizontal={!isMobile}
                                    >
                                        <Stack.Item grow={1}>
                                            <TextField
                                                label="Environment Name"
                                                value={environmentData.name}
                                                readOnly
                                                disabled
                                            />
                                        </Stack.Item>
                                        <Stack.Item grow={1}>
                                            <Stack
                                                horizontal={!isMobile}
                                                tokens={{ childrenGap: 8 }}
                                                verticalAlign="end"
                                            >
                                                <Stack.Item grow={1}>
                                                    <Dropdown
                                                        label="Active Operation Mode"
                                                        selectedKey={
                                                            environmentData.activeOperationalModeName ||
                                                            'Normal'
                                                        }
                                                        options={[
                                                            {
                                                                key: 'Normal',
                                                                text: 'Normal',
                                                            },
                                                            ...environmentData.operationModes.map(
                                                                (om) => ({
                                                                    key: om.name,
                                                                    text: om.name,
                                                                })
                                                            ),
                                                        ]}
                                                        onChange={(
                                                            _,
                                                            option
                                                        ) => {
                                                            if (option) {
                                                                handleOperationModeChange(
                                                                    option.key as string
                                                                );
                                                            }
                                                        }}
                                                    />
                                                </Stack.Item>
                                                <TooltipHost content="Changing the operation mode affects real-time data flow and multiple system configurations">
                                                    <Icon
                                                        iconName="Warning"
                                                        styles={{
                                                            root: {
                                                                color: theme
                                                                    .palette
                                                                    .orange,
                                                                fontSize: 20,
                                                                cursor: 'help',
                                                                marginBottom:
                                                                    isMobile
                                                                        ? '0px'
                                                                        : '8px',
                                                            },
                                                        }}
                                                    />
                                                </TooltipHost>
                                            </Stack>
                                        </Stack.Item>
                                    </Stack>

                                    {/* Active Configuration Preview */}
                                    <ActiveConfigurationPreview
                                        environmentData={environmentData}
                                        isExpanded={
                                            expandedSections.activePreview
                                        }
                                        onToggleExpand={() =>
                                            toggleSection('activePreview')
                                        }
                                    />
                                </>
                            )}
                    </div>
                </div>

                {/* Dialogs - Always available */}
                <Dialog
                    hidden={!showUnsavedChangesDialog}
                    onDismiss={cancelEnvironmentChange}
                    dialogContentProps={{
                        type: DialogType.normal,
                        title: 'Unsaved Changes',
                        subText: `You have unsaved changes in "${selectedEnvironmentName}". Switching to "${pendingEnvironmentChange}" will discard these changes. Do you want to continue?`,
                    }}
                    modalProps={{
                        isBlocking: true,
                    }}
                >
                    <MessageBar
                        messageBarType={MessageBarType.warning}
                        isMultiline={true}
                        styles={{
                            root: {
                                backgroundColor: theme.palette.neutralLighter,
                                borderLeft: `4px solid ${theme.palette.yellowDark}`,
                            },
                            text: {
                                color: theme.palette.neutralPrimary,
                            },
                            icon: {
                                color: theme.palette.yellowDark,
                            },
                        }}
                    >
                        All unsaved changes will be lost if you continue.
                    </MessageBar>
                    <DialogFooter>
                        <PrimaryButton
                            onClick={confirmEnvironmentChange}
                            text="Discard Changes"
                        />
                        <DefaultButton
                            onClick={cancelEnvironmentChange}
                            text="Cancel"
                        />
                    </DialogFooter>
                </Dialog>

                {/* Operation Mode Confirmation Dialog */}
                <Dialog
                    hidden={!showOperationModeDialog}
                    onDismiss={cancelOperationModeChange}
                    dialogContentProps={{
                        type: DialogType.normal,
                        title: 'Confirm Operation Mode Change',
                        subText: `Are you sure you want to change the operation mode from "${environmentData?.activeOperationalModeName}" to "${pendingOperationMode}"? This will affect real-time data flow and multiple system configurations.`,
                    }}
                    modalProps={{
                        isBlocking: true,
                    }}
                >
                    <MessageBar
                        messageBarType={MessageBarType.warning}
                        isMultiline={true}
                    >
                        <strong>Warning:</strong> Changing the operation mode is
                        a critical action that will:
                        <ul style={{ marginTop: '8px', marginBottom: '8px' }}>
                            <li>Modify active data channels</li>
                            <li>Change feature flag configurations</li>
                            <li>Affect real-time data synchronization</li>
                            <li>Impact downstream services</li>
                        </ul>
                        Make sure you understand the implications before
                        proceeding.
                    </MessageBar>
                    <DialogFooter>
                        <PrimaryButton
                            onClick={confirmOperationModeChange}
                            text="Confirm Change"
                        />
                        <DefaultButton
                            onClick={cancelOperationModeChange}
                            text="Cancel"
                        />
                    </DialogFooter>
                </Dialog>

                {/* Add From URL Dialog */}
                <Dialog
                    hidden={!showAddFromUrlDialog}
                    onDismiss={cancelAddFromUrl}
                    dialogContentProps={{
                        type: DialogType.normal,
                        title:
                            urlDialogSource === 'featureFlag'
                                ? 'Add Flag Override From Azure DevOps URL'
                                : 'Add Filter From Azure DevOps URL',
                    }}
                    modalProps={{
                        isBlocking: false,
                    }}
                    minWidth={600}
                >
                    <Stack tokens={{ childrenGap: 16 }}>
                        <TextField
                            label="Azure DevOps URL"
                            placeholder="https://dev.azure.com/msazure/One/_build?definitionId=375163"
                            value={urlInput}
                            onChange={(_, newValue) =>
                                handleUrlInputChange(newValue || '')
                            }
                            required
                            multiline
                            rows={2}
                            description="Paste a YAML build URL (definitionId) or Classic release URL (releaseId)"
                            styles={placeholderStyles}
                        />

                        <TextField
                            label="Branch Name"
                            placeholder="e.g., main or dcmt.*"
                            value={urlBranchInput}
                            onChange={(_, newValue) =>
                                setUrlBranchInput(newValue || '')
                            }
                            description="Branch name (supports regex patterns)"
                            styles={placeholderStyles}
                        />

                        {urlParseError && (
                            <MessageBar
                                messageBarType={MessageBarType.error}
                                styles={{
                                    root: {
                                        backgroundColor: theme.palette.redDark,
                                    },
                                    text: { color: theme.palette.white },
                                    icon: { color: theme.palette.white },
                                }}
                            >
                                {urlParseError}
                            </MessageBar>
                        )}

                        {parsedUrlInfo && (
                            <div
                                style={{
                                    padding: '12px',
                                    backgroundColor:
                                        theme.palette.neutralLighterAlt,
                                    borderRadius: '4px',
                                }}
                            >
                                <Text
                                    variant="mediumPlus"
                                    styles={{
                                        root: {
                                            fontWeight: 600,
                                            marginBottom: '8px',
                                            display: 'block',
                                        },
                                    }}
                                >
                                    Preview
                                </Text>
                                <Stack tokens={{ childrenGap: 4 }}>
                                    <Text variant="small">
                                        <strong>Pipeline Type:</strong>{' '}
                                        {parsedUrlInfo.pipelineSourceType === 1
                                            ? 'YAML'
                                            : 'Classic'}
                                    </Text>
                                    <Text variant="small">
                                        <strong>Account:</strong>{' '}
                                        {parsedUrlInfo.accountName}
                                    </Text>
                                    <Text variant="small">
                                        <strong>Project:</strong>{' '}
                                        {parsedUrlInfo.projectName}
                                    </Text>
                                    <Text variant="small">
                                        <strong>Definition/Release ID:</strong>{' '}
                                        {parsedUrlInfo.releaseDefinitionId}
                                    </Text>
                                    <Text variant="small">
                                        <strong>Branch:</strong>{' '}
                                        {urlBranchInput || '(not set)'}
                                    </Text>
                                </Stack>
                            </div>
                        )}
                    </Stack>
                    <DialogFooter>
                        <PrimaryButton
                            onClick={confirmAddFromUrl}
                            text="Add Filter"
                            disabled={!parsedUrlInfo}
                        />
                        <DefaultButton
                            onClick={cancelAddFromUrl}
                            text="Cancel"
                        />
                    </DialogFooter>
                </Dialog>

                {/* Add From Clipboard Dialog */}
                <Dialog
                    hidden={!showAddFromClipboardDialog}
                    onDismiss={cancelAddFromClipboard}
                    dialogContentProps={{
                        type: DialogType.normal,
                        title: 'Add Filter From Clipboard',
                    }}
                    modalProps={{
                        isBlocking: false,
                    }}
                    minWidth={600}
                >
                    <Stack tokens={{ childrenGap: 16 }}>
                        <TextField
                            label="Clipboard Data"
                            placeholder="Paste the encoded filter data here..."
                            value={clipboardInput}
                            onChange={(_, newValue) =>
                                handleClipboardInputChange(newValue || '')
                            }
                            required
                            multiline
                            rows={6}
                            description="Paste the filter data copied from 'Copy to Clipboard'"
                            styles={placeholderStyles}
                        />

                        {clipboardDecodeError && (
                            <MessageBar
                                messageBarType={MessageBarType.error}
                                styles={{
                                    root: {
                                        backgroundColor: theme.palette.redDark,
                                    },
                                    text: { color: theme.palette.white },
                                    icon: { color: theme.palette.white },
                                }}
                            >
                                {clipboardDecodeError}
                            </MessageBar>
                        )}

                        {decodedClipboardData && (
                            <div
                                style={{
                                    padding: '12px',
                                    backgroundColor:
                                        theme.palette.neutralLighterAlt,
                                    borderRadius: '4px',
                                }}
                            >
                                <Text
                                    variant="mediumPlus"
                                    styles={{
                                        root: {
                                            fontWeight: 600,
                                            marginBottom: '8px',
                                            display: 'block',
                                        },
                                    }}
                                >
                                    Preview
                                </Text>
                                <Stack tokens={{ childrenGap: 4 }}>
                                    <Text variant="small">
                                        <strong>Name:</strong>{' '}
                                        {decodedClipboardData.name}
                                    </Text>
                                    <Text variant="small">
                                        <strong>Enabled:</strong>{' '}
                                        {decodedClipboardData.enabled
                                            ? 'Yes'
                                            : 'No'}
                                    </Text>
                                    <Text variant="small">
                                        <strong>Active:</strong>{' '}
                                        {decodedClipboardData.active
                                            ? 'Yes'
                                            : 'No'}
                                    </Text>
                                    {decodedClipboardData.serviceTreeId && (
                                        <Text variant="small">
                                            <strong>Service Tree ID:</strong>{' '}
                                            {decodedClipboardData.serviceTreeId}
                                        </Text>
                                    )}
                                    {decodedClipboardData.accountName && (
                                        <Text variant="small">
                                            <strong>Account:</strong>{' '}
                                            {decodedClipboardData.accountName}
                                        </Text>
                                    )}
                                    {decodedClipboardData.projectName && (
                                        <Text variant="small">
                                            <strong>Project:</strong>{' '}
                                            {decodedClipboardData.projectName}
                                        </Text>
                                    )}
                                    {decodedClipboardData.branchName && (
                                        <Text variant="small">
                                            <strong>Branch:</strong>{' '}
                                            {decodedClipboardData.branchName}
                                        </Text>
                                    )}
                                    {decodedClipboardData.releaseDefinitionId && (
                                        <Text variant="small">
                                            <strong>
                                                Release Definition ID:
                                            </strong>{' '}
                                            {
                                                decodedClipboardData.releaseDefinitionId
                                            }
                                        </Text>
                                    )}
                                    {decodedClipboardData.pipeline !== null &&
                                        decodedClipboardData.pipeline !==
                                            undefined && (
                                            <Text variant="small">
                                                <strong>Pipeline:</strong>{' '}
                                                {
                                                    Pipeline[
                                                        decodedClipboardData
                                                            .pipeline
                                                    ]
                                                }
                                            </Text>
                                        )}
                                    {decodedClipboardData.region !== null &&
                                        decodedClipboardData.region !==
                                            undefined && (
                                            <Text variant="small">
                                                <strong>Region:</strong>{' '}
                                                {
                                                    Region[
                                                        decodedClipboardData
                                                            .region
                                                    ]
                                                }
                                            </Text>
                                        )}
                                    {decodedClipboardData.workLane !== null &&
                                        decodedClipboardData.workLane !==
                                            undefined && (
                                            <Text variant="small">
                                                <strong>Work Lane:</strong>{' '}
                                                {
                                                    WorkLane[
                                                        decodedClipboardData
                                                            .workLane
                                                    ]
                                                }
                                            </Text>
                                        )}
                                    {decodedClipboardData.pipelineSourceType !==
                                        null &&
                                        decodedClipboardData.pipelineSourceType !==
                                            undefined && (
                                            <Text variant="small">
                                                <strong>
                                                    Pipeline Source:
                                                </strong>{' '}
                                                {
                                                    ReleasePipelineSource[
                                                        decodedClipboardData
                                                            .pipelineSourceType
                                                    ]
                                                }
                                            </Text>
                                        )}
                                    {decodedClipboardData.tags &&
                                        decodedClipboardData.tags.length >
                                            0 && (
                                            <Text variant="small">
                                                <strong>Tags:</strong>{' '}
                                                {decodedClipboardData.tags.join(
                                                    ', '
                                                )}
                                            </Text>
                                        )}
                                    {decodedClipboardData.startTime && (
                                        <Text variant="small">
                                            <strong>Start Time:</strong>{' '}
                                            {new Date(
                                                decodedClipboardData.startTime
                                            ).toLocaleString()}
                                        </Text>
                                    )}
                                    {decodedClipboardData.endTime && (
                                        <Text variant="small">
                                            <strong>End Time:</strong>{' '}
                                            {new Date(
                                                decodedClipboardData.endTime
                                            ).toLocaleString()}
                                        </Text>
                                    )}
                                </Stack>
                            </div>
                        )}
                    </Stack>
                    <DialogFooter>
                        <PrimaryButton
                            onClick={confirmAddFromClipboard}
                            text="Add Filter"
                            disabled={!decodedClipboardData}
                        />
                        <DefaultButton
                            onClick={cancelAddFromClipboard}
                            text="Cancel"
                        />
                    </DialogFooter>
                </Dialog>

                {/* Add Flag Override From Clipboard Dialog */}
                <Dialog
                    hidden={!showFlagClipboardDialog}
                    onDismiss={cancelFlagClipboard}
                    dialogContentProps={{
                        type: DialogType.normal,
                        title: 'Add Flag Override From Clipboard',
                    }}
                    modalProps={{
                        isBlocking: false,
                    }}
                    minWidth={600}
                >
                    <Stack tokens={{ childrenGap: 16 }}>
                        <TextField
                            label="Clipboard Data"
                            placeholder="Paste the encoded flag override data here..."
                            value={flagClipboardInput}
                            onChange={(_, newValue) =>
                                handleFlagClipboardInputChange(newValue || '')
                            }
                            required
                            multiline
                            rows={6}
                            description="Paste the flag override data copied from 'Copy to Clipboard'"
                            styles={placeholderStyles}
                        />

                        {flagClipboardDecodeError && (
                            <MessageBar
                                messageBarType={MessageBarType.error}
                                styles={{
                                    root: {
                                        backgroundColor: theme.palette.redDark,
                                    },
                                    text: { color: theme.palette.white },
                                    icon: { color: theme.palette.white },
                                }}
                            >
                                {flagClipboardDecodeError}
                            </MessageBar>
                        )}

                        {decodedFlagClipboardData && (
                            <div
                                style={{
                                    padding: '12px',
                                    backgroundColor:
                                        theme.palette.neutralLighterAlt,
                                    borderRadius: '4px',
                                }}
                            >
                                <Text
                                    variant="mediumPlus"
                                    styles={{
                                        root: {
                                            fontWeight: 600,
                                            marginBottom: '8px',
                                            display: 'block',
                                        },
                                    }}
                                >
                                    Preview
                                </Text>
                                <Stack tokens={{ childrenGap: 4 }}>
                                    <Text variant="small">
                                        <strong>Name:</strong>{' '}
                                        {decodedFlagClipboardData.name}
                                    </Text>
                                    <Text variant="small">
                                        <strong>Enabled:</strong>{' '}
                                        {decodedFlagClipboardData.enabled
                                            ? 'Yes'
                                            : 'No'}
                                    </Text>
                                    <Text variant="small">
                                        <strong>Active:</strong>{' '}
                                        {decodedFlagClipboardData.active
                                            ? 'Yes'
                                            : 'No'}
                                    </Text>
                                    {decodedFlagClipboardData.serviceTreeId && (
                                        <Text variant="small">
                                            <strong>Service Tree ID:</strong>{' '}
                                            {
                                                decodedFlagClipboardData.serviceTreeId
                                            }
                                        </Text>
                                    )}
                                    {decodedFlagClipboardData.accountName && (
                                        <Text variant="small">
                                            <strong>Account:</strong>{' '}
                                            {
                                                decodedFlagClipboardData.accountName
                                            }
                                        </Text>
                                    )}
                                    {decodedFlagClipboardData.projectName && (
                                        <Text variant="small">
                                            <strong>Project:</strong>{' '}
                                            {
                                                decodedFlagClipboardData.projectName
                                            }
                                        </Text>
                                    )}
                                    {decodedFlagClipboardData.branchName && (
                                        <Text variant="small">
                                            <strong>Branch:</strong>{' '}
                                            {
                                                decodedFlagClipboardData.branchName
                                            }
                                        </Text>
                                    )}
                                    {decodedFlagClipboardData.releaseDefinitionId && (
                                        <Text variant="small">
                                            <strong>
                                                Release Definition ID:
                                            </strong>{' '}
                                            {
                                                decodedFlagClipboardData.releaseDefinitionId
                                            }
                                        </Text>
                                    )}
                                    {decodedFlagClipboardData.pipelineSourceType !==
                                        null &&
                                        decodedFlagClipboardData.pipelineSourceType !==
                                            undefined && (
                                            <Text variant="small">
                                                <strong>
                                                    Pipeline Source:
                                                </strong>{' '}
                                                {
                                                    ReleasePipelineSource[
                                                        decodedFlagClipboardData
                                                            .pipelineSourceType
                                                    ]
                                                }
                                            </Text>
                                        )}
                                    {decodedFlagClipboardData.tags &&
                                        decodedFlagClipboardData.tags.length >
                                            0 && (
                                            <Text variant="small">
                                                <strong>Tags:</strong>{' '}
                                                {decodedFlagClipboardData.tags.join(
                                                    ', '
                                                )}
                                            </Text>
                                        )}
                                    {decodedFlagClipboardData.startTime && (
                                        <Text variant="small">
                                            <strong>Start Time:</strong>{' '}
                                            {new Date(
                                                decodedFlagClipboardData.startTime
                                            ).toLocaleString()}
                                        </Text>
                                    )}
                                    {decodedFlagClipboardData.endTime && (
                                        <Text variant="small">
                                            <strong>End Time:</strong>{' '}
                                            {new Date(
                                                decodedFlagClipboardData.endTime
                                            ).toLocaleString()}
                                        </Text>
                                    )}
                                    {decodedFlagClipboardData.featureFlags && (
                                        <>
                                            <Text
                                                variant="small"
                                                styles={{
                                                    root: {
                                                        fontWeight: 600,
                                                        marginTop: '4px',
                                                    },
                                                }}
                                            >
                                                Feature Flags:
                                            </Text>
                                            {Object.entries(
                                                decodedFlagClipboardData.featureFlags
                                            )
                                                .filter(
                                                    ([, v]) =>
                                                        v !== null &&
                                                        v !== undefined
                                                )
                                                .map(([key, value]) => (
                                                    <Text
                                                        key={key}
                                                        variant="small"
                                                        styles={{
                                                            root: {
                                                                paddingLeft:
                                                                    '8px',
                                                            },
                                                        }}
                                                    >
                                                        {key}:{' '}
                                                        {value
                                                            ? 'True'
                                                            : 'False'}
                                                    </Text>
                                                ))}
                                        </>
                                    )}
                                </Stack>
                            </div>
                        )}
                    </Stack>
                    <DialogFooter>
                        <PrimaryButton
                            onClick={confirmFlagClipboard}
                            text="Add Flag Override"
                            disabled={!decodedFlagClipboardData}
                        />
                        <DefaultButton
                            onClick={cancelFlagClipboard}
                            text="Cancel"
                        />
                    </DialogFooter>
                </Dialog>

                {/* CSV Import Dialog for Feature Flag Overrides */}
                <CsvImportDialog
                    hidden={!showFlagCsvImportDialog}
                    onDismiss={() => setShowFlagCsvImportDialog(false)}
                    onImport={handleImportFlagOverridesCsv}
                    existingCount={
                        environmentData?.normalOperationMode
                            ?.featureFlagsOverrides?.length ?? 0
                    }
                />

                {/* CSV Import Dialog for Data Channel Overrides */}
                <DataChannelCsvImportDialog
                    hidden={!showChannelCsvImportDialog}
                    onDismiss={() => setShowChannelCsvImportDialog(false)}
                    onImport={handleImportChannelOverridesCsv}
                    existingCount={
                        environmentData?.normalOperationMode
                            ?.dataChannelOverrides?.length ?? 0
                    }
                />

                {/* Additional Content Containers - Only show when data is loaded */}
                {!isLoadingEnvironments && !isLoading && environmentData && (
                    <>
                        {/* Container 2: Normal Operation Mode */}
                        <div
                            style={{
                                backgroundColor: theme.palette.white,
                                borderRadius: '4px',
                                boxShadow:
                                    '0 3.2px 7.2px 0 rgba(0, 0, 0, .132), 0 .6px 1.8px 0 rgba(0, 0, 0, .108)',
                                overflow: 'hidden',
                                marginBottom: '16px',
                            }}
                        >
                            {/* Header */}
                            <div
                                style={{
                                    padding: isMobile ? '16px' : '16px 20px',
                                    backgroundColor:
                                        theme.palette.neutralQuaternary,
                                    borderBottom: `2px solid ${theme.palette.themeDark}`,
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    gap: '16px',
                                    userSelect: 'none',
                                    cursor: 'pointer',
                                }}
                                onClick={() => toggleSection('normal')}
                            >
                                <Text
                                    variant="xLarge"
                                    styles={{
                                        root: {
                                            fontWeight: 700,
                                            color: theme.palette.black,
                                        },
                                    }}
                                >
                                    Normal Operation Mode
                                </Text>
                                <IconButton
                                    iconProps={{
                                        iconName: expandedSections.normal
                                            ? 'ChevronUp'
                                            : 'ChevronDown',
                                    }}
                                    title={
                                        expandedSections.normal
                                            ? 'Collapse'
                                            : 'Expand'
                                    }
                                    ariaLabel={`${
                                        expandedSections.normal
                                            ? 'Collapse'
                                            : 'Expand'
                                    } Normal Operation Mode`}
                                    aria-expanded={expandedSections.normal}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        toggleSection('normal');
                                    }}
                                />
                            </div>

                            {/* Content */}
                            {expandedSections.normal && (
                                <div
                                    style={{
                                        padding: isMobile ? '16px' : '20px',
                                    }}
                                >
                                    <NormalOperationMode
                                        mode={
                                            environmentData.normalOperationMode
                                        }
                                        onIsEnabledChange={(value) =>
                                            updateNormalModeField(
                                                'isEnabled',
                                                value
                                            )
                                        }
                                        onPipelineChange={(value) =>
                                            updateNormalModeField(
                                                'pipeline',
                                                value
                                            )
                                        }
                                        onRegionChange={(value) =>
                                            updateNormalModeField(
                                                'region',
                                                value
                                            )
                                        }
                                        onWorklaneChange={(value) =>
                                            updateNormalModeField(
                                                'worklane',
                                                value
                                            )
                                        }
                                        onFeatureFlagChange={(flagKey, value) =>
                                            updateFeatureFlag(
                                                'normal',
                                                null,
                                                flagKey,
                                                value
                                            )
                                        }
                                    />
                                </div>
                            )}
                        </div>

                        {/* Container 3: Data Channel Overrides */}
                        <div
                            style={{
                                backgroundColor: theme.palette.white,
                                borderRadius: '4px',
                                boxShadow:
                                    '0 3.2px 7.2px 0 rgba(0, 0, 0, .132), 0 .6px 1.8px 0 rgba(0, 0, 0, .108)',
                                overflow: 'hidden',
                                marginBottom: '16px',
                            }}
                        >
                            {/* Header */}
                            <div
                                style={{
                                    padding: isMobile ? '16px' : '16px 20px',
                                    backgroundColor:
                                        theme.palette.neutralQuaternary,
                                    borderBottom: `2px solid ${theme.palette.themeDark}`,
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    gap: '16px',
                                    userSelect: 'none',
                                    cursor: 'pointer',
                                }}
                                onClick={() => toggleSection('overrides')}
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                    }}
                                >
                                    <Text
                                        variant="xLarge"
                                        styles={{
                                            root: {
                                                fontWeight: 700,
                                                color: theme.palette.black,
                                            },
                                        }}
                                    >
                                        Data Channel Overrides
                                    </Text>
                                    <TooltipHost content="Most frequently used section - configure channel routing overrides">
                                        <Icon
                                            iconName="Info"
                                            styles={{
                                                root: {
                                                    color: theme.palette
                                                        .themePrimary,
                                                    fontSize: 16,
                                                },
                                            }}
                                        />
                                    </TooltipHost>
                                </div>
                                <IconButton
                                    iconProps={{
                                        iconName: expandedSections.overrides
                                            ? 'ChevronUp'
                                            : 'ChevronDown',
                                    }}
                                    title={
                                        expandedSections.overrides
                                            ? 'Collapse'
                                            : 'Expand'
                                    }
                                    ariaLabel={`${
                                        expandedSections.overrides
                                            ? 'Collapse'
                                            : 'Expand'
                                    } Data Channel Overrides`}
                                    aria-expanded={expandedSections.overrides}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        toggleSection('overrides');
                                    }}
                                />
                            </div>

                            {/* Content */}
                            {expandedSections.overrides && (
                                <div
                                    style={{
                                        padding: isMobile ? '16px' : '20px',
                                    }}
                                >
                                    <DataChannelOverridesList
                                        overrides={
                                            environmentData.normalOperationMode
                                                .dataChannelOverrides
                                        }
                                        searchText={searchText}
                                        filterPipeline={filterPipeline}
                                        filterRegion={filterRegion}
                                        filterWorkLane={filterWorkLane}
                                        filterEnabled={filterEnabled}
                                        collapsedOverrides={collapsedOverrides}
                                        overrideFilterTypes={
                                            overrideFilterTypes
                                        }
                                        onSearchTextChange={setSearchText}
                                        onFilterPipelineChange={
                                            setFilterPipeline
                                        }
                                        onFilterRegionChange={setFilterRegion}
                                        onFilterWorkLaneChange={
                                            setFilterWorkLane
                                        }
                                        onFilterEnabledChange={setFilterEnabled}
                                        onClearFilters={clearFilters}
                                        onToggleCollapse={
                                            toggleOverrideCollapse
                                        }
                                        onFilterTypeChange={
                                            handleFilterTypeChange
                                        }
                                        onFieldChange={updateOverrideField}
                                        onCopyOverride={copyOverride}
                                        onCopyToClipboard={
                                            copyFilterToClipboard
                                        }
                                        onRemoveOverride={handleRemoveOverride}
                                        onAddOverride={handleAddOverride}
                                        onAddFromUrl={() => {
                                            setUrlDialogSource('dataChannel');
                                            setShowAddFromUrlDialog(true);
                                        }}
                                        onAddFromClipboard={() =>
                                            setShowAddFromClipboardDialog(true)
                                        }
                                        onExportCsv={
                                            handleExportChannelOverridesCsv
                                        }
                                        onImportCsv={() =>
                                            setShowChannelCsvImportDialog(true)
                                        }
                                        isOverrideChanged={isOverrideChanged}
                                        isOverrideActive={isOverrideActive}
                                        filterOverrides={filterOverrides}
                                    />
                                </div>
                            )}
                        </div>

                        {/* Container 3.5: Feature Flag Overrides */}
                        <div
                            style={{
                                backgroundColor: theme.palette.white,
                                borderRadius: '4px',
                                boxShadow:
                                    '0 3.2px 7.2px 0 rgba(0, 0, 0, .132), 0 .6px 1.8px 0 rgba(0, 0, 0, .108)',
                                overflow: 'hidden',
                                marginBottom: '16px',
                            }}
                        >
                            {/* Header */}
                            <div
                                style={{
                                    padding: isMobile ? '16px' : '16px 20px',
                                    backgroundColor:
                                        theme.palette.neutralQuaternary,
                                    borderBottom: `2px solid ${theme.palette.themeDark}`,
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    gap: '16px',
                                    userSelect: 'none',
                                    cursor: 'pointer',
                                }}
                                onClick={() => toggleSection('flagOverrides')}
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                    }}
                                >
                                    <Text
                                        variant="xLarge"
                                        styles={{
                                            root: {
                                                fontWeight: 700,
                                                color: theme.palette.black,
                                            },
                                        }}
                                    >
                                        Feature Flag Overrides
                                    </Text>
                                    <TooltipHost content="Configure per-deployment feature flag overrides">
                                        <Icon
                                            iconName="Info"
                                            styles={{
                                                root: {
                                                    color: theme.palette
                                                        .themePrimary,
                                                    fontSize: 16,
                                                },
                                            }}
                                        />
                                    </TooltipHost>
                                </div>
                                <IconButton
                                    iconProps={{
                                        iconName: expandedSections.flagOverrides
                                            ? 'ChevronUp'
                                            : 'ChevronDown',
                                    }}
                                    title={
                                        expandedSections.flagOverrides
                                            ? 'Collapse'
                                            : 'Expand'
                                    }
                                    ariaLabel={`${
                                        expandedSections.flagOverrides
                                            ? 'Collapse'
                                            : 'Expand'
                                    } Feature Flag Overrides`}
                                    aria-expanded={
                                        expandedSections.flagOverrides
                                    }
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        toggleSection('flagOverrides');
                                    }}
                                />
                            </div>

                            {/* Content */}
                            {expandedSections.flagOverrides && (
                                <div
                                    style={{
                                        padding: isMobile ? '16px' : '20px',
                                    }}
                                >
                                    <FeatureFlagOverridesList
                                        overrides={
                                            environmentData.normalOperationMode
                                                .featureFlagsOverrides || []
                                        }
                                        searchText={flagSearchText}
                                        filterEnabled={flagFilterEnabled}
                                        collapsedOverrides={
                                            collapsedFlagOverrides
                                        }
                                        overrideFilterTypes={
                                            flagOverrideFilterTypes
                                        }
                                        onSearchTextChange={setFlagSearchText}
                                        onFilterEnabledChange={
                                            setFlagFilterEnabled
                                        }
                                        onClearFilters={clearFlagFilters}
                                        onToggleCollapse={
                                            toggleFlagOverrideCollapse
                                        }
                                        onFilterTypeChange={
                                            handleFlagFilterTypeChange
                                        }
                                        onFieldChange={updateFlagOverrideField}
                                        onFeatureFlagChange={
                                            updateFlagOverrideFeatureFlag
                                        }
                                        onCopyOverride={copyFlagOverride}
                                        onCopyToClipboard={
                                            copyFlagFilterToClipboard
                                        }
                                        onRemoveOverride={
                                            handleRemoveFlagOverride
                                        }
                                        onAddOverride={handleAddFlagOverride}
                                        onAddFromUrl={() => {
                                            setUrlDialogSource('featureFlag');
                                            setShowAddFromUrlDialog(true);
                                        }}
                                        onAddFromClipboard={() =>
                                            setShowFlagClipboardDialog(true)
                                        }
                                        onExportCsv={
                                            handleExportFlagOverridesCsv
                                        }
                                        onImportCsv={() =>
                                            setShowFlagCsvImportDialog(true)
                                        }
                                        isOverrideChanged={
                                            isFlagOverrideChanged
                                        }
                                        isOverrideActive={isFlagOverrideActive}
                                        filterOverrides={filterFlagOverrides}
                                    />
                                </div>
                            )}
                        </div>

                        {/* Container 4: Active Channels */}
                        <div
                            style={{
                                backgroundColor: theme.palette.white,
                                borderRadius: '4px',
                                boxShadow:
                                    '0 3.2px 7.2px 0 rgba(0, 0, 0, .132), 0 .6px 1.8px 0 rgba(0, 0, 0, .108)',
                                overflow: 'hidden',
                                marginBottom: '16px',
                            }}
                        >
                            {/* Header */}
                            <div
                                style={{
                                    padding: isMobile ? '16px' : '16px 20px',
                                    backgroundColor:
                                        theme.palette.neutralQuaternary,
                                    borderBottom: `2px solid ${theme.palette.themeDark}`,
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    gap: '16px',
                                    userSelect: 'none',
                                    cursor: 'pointer',
                                }}
                                onClick={() => toggleSection('channels')}
                            >
                                <Text
                                    variant="xLarge"
                                    styles={{
                                        root: {
                                            fontWeight: 700,
                                            color: theme.palette.black,
                                        },
                                    }}
                                >
                                    Active Channels (
                                    {
                                        environmentData.normalOperationMode
                                            .channels.length
                                    }
                                    )
                                </Text>
                                <IconButton
                                    iconProps={{
                                        iconName: expandedSections.channels
                                            ? 'ChevronUp'
                                            : 'ChevronDown',
                                    }}
                                    title={
                                        expandedSections.channels
                                            ? 'Collapse'
                                            : 'Expand'
                                    }
                                    ariaLabel={`${
                                        expandedSections.channels
                                            ? 'Collapse'
                                            : 'Expand'
                                    } Active Channels`}
                                    aria-expanded={expandedSections.channels}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        toggleSection('channels');
                                    }}
                                />
                            </div>

                            {/* Content */}
                            {expandedSections.channels && (
                                <div
                                    style={{
                                        padding: isMobile ? '16px' : '20px',
                                    }}
                                >
                                    <ActiveChannelsList
                                        channels={
                                            environmentData.normalOperationMode
                                                .channels
                                        }
                                    />
                                </div>
                            )}
                        </div>

                        {/* Container 5: Operation Modes */}
                        <div
                            style={{
                                backgroundColor: theme.palette.white,
                                borderRadius: '4px',
                                boxShadow:
                                    '0 3.2px 7.2px 0 rgba(0, 0, 0, .132), 0 .6px 1.8px 0 rgba(0, 0, 0, .108)',
                                overflow: 'hidden',
                                marginBottom: '16px',
                            }}
                        >
                            {/* Header */}
                            <div
                                style={{
                                    padding: isMobile ? '16px' : '16px 20px',
                                    backgroundColor:
                                        theme.palette.neutralQuaternary,
                                    borderBottom: `2px solid ${theme.palette.themeDark}`,
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    gap: '16px',
                                    userSelect: 'none',
                                    cursor: 'pointer',
                                }}
                                onClick={() => toggleSection('operations')}
                            >
                                <Text
                                    variant="xLarge"
                                    styles={{
                                        root: {
                                            fontWeight: 700,
                                            color: theme.palette.black,
                                        },
                                    }}
                                >
                                    Operation Modes (
                                    {environmentData.operationModes.length})
                                </Text>
                                <IconButton
                                    iconProps={{
                                        iconName: expandedSections.operations
                                            ? 'ChevronUp'
                                            : 'ChevronDown',
                                    }}
                                    title={
                                        expandedSections.operations
                                            ? 'Collapse'
                                            : 'Expand'
                                    }
                                    ariaLabel={`${
                                        expandedSections.operations
                                            ? 'Collapse'
                                            : 'Expand'
                                    } Operation Modes`}
                                    aria-expanded={expandedSections.operations}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        toggleSection('operations');
                                    }}
                                />
                            </div>

                            {/* Content */}
                            {expandedSections.operations && (
                                <div
                                    style={{
                                        padding: isMobile ? '16px' : '20px',
                                    }}
                                >
                                    <OperationModesList
                                        modes={environmentData.operationModes}
                                        onIsEnabledChange={(modeIndex, value) =>
                                            updateOperationModeField(
                                                modeIndex,
                                                'isEnabled',
                                                value
                                            )
                                        }
                                        onPipelineChange={(modeIndex, value) =>
                                            updateOperationModeField(
                                                modeIndex,
                                                'pipeline',
                                                value
                                            )
                                        }
                                        onRegionChange={(modeIndex, value) =>
                                            updateOperationModeField(
                                                modeIndex,
                                                'region',
                                                value
                                            )
                                        }
                                        onWorklaneChange={(modeIndex, value) =>
                                            updateOperationModeField(
                                                modeIndex,
                                                'worklane',
                                                value
                                            )
                                        }
                                        onFeatureFlagChange={(
                                            modeIndex,
                                            flagKey,
                                            value
                                        ) =>
                                            updateFeatureFlag(
                                                'operation',
                                                modeIndex,
                                                flagKey,
                                                value
                                            )
                                        }
                                    />
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};
